import DOMPurify from 'dompurify';

const ALLOWED_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'a', 'ul', 'ol', 'li', 'h2', 'h3', 'h4', 'div'];
const ALLOWED_ATTR = ['href', 'target', 'rel'];
const SAFE_URI_PATTERN = /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i;
const BLOCK_TAGS = new Set(['p', 'div', 'h2', 'h3', 'h4', 'ul', 'ol', 'li']);

let hooksConfigured = false;

function configureHooks() {
  if (hooksConfigured) {
    return;
  }

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.getAttribute('href')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });

  hooksConfigured = true;
}

export function sanitizeJobHtml(html = '') {
  configureHooks();

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP: SAFE_URI_PATTERN,
  });
}

function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function isSafeHref(value = '') {
  return /^(https?:\/\/|mailto:)/i.test(String(value || '').trim());
}

function hasBoldStyle(style = '') {
  return /font-weight\s*:\s*(bold|bolder|[6-9]00)/i.test(style);
}

function hasItalicStyle(style = '') {
  return /font-style\s*:\s*italic/i.test(style);
}

function getSafeTagName(tagName = '') {
  const normalized = tagName.toLowerCase();

  if (normalized === 'h1') {
    return 'h2';
  }

  if (normalized === 'h5' || normalized === 'h6') {
    return 'h4';
  }

  if (ALLOWED_TAGS.includes(normalized)) {
    return normalized;
  }

  return '';
}

function serializePastedNode(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    return escapeHtml(node.textContent || '');
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }

  const originalTag = node.tagName.toLowerCase();
  const tag = getSafeTagName(originalTag);

  if (tag === 'style' || tag === 'script' || tag === 'iframe') {
    return '';
  }

  if (tag === 'br') {
    return '<br>';
  }

  let children = Array.from(node.childNodes).map(serializePastedNode).join('');
  const style = node.getAttribute('style') || '';

  if (!children.trim() && BLOCK_TAGS.has(tag)) {
    children = '<br>';
  }

  if (!tag) {
    if (hasItalicStyle(style)) {
      children = `<em>${children}</em>`;
    }

    if (hasBoldStyle(style)) {
      children = `<strong>${children}</strong>`;
    }

    return children;
  }

  if (tag === 'a') {
    const href = node.getAttribute('href') || '';
    const hrefAttribute = isSafeHref(href) ? ` href="${escapeHtml(href)}"` : '';
    return `<a${hrefAttribute} target="_blank" rel="noopener noreferrer">${children}</a>`;
  }

  let content = children;
  if (!['strong', 'b'].includes(tag) && hasBoldStyle(style)) {
    content = `<strong>${content}</strong>`;
  }

  if (!['em', 'i'].includes(tag) && hasItalicStyle(style)) {
    content = `<em>${content}</em>`;
  }

  return `<${tag}>${content}</${tag}>`;
}

export function normalizeClipboardHtml(html = '') {
  if (!html.trim()) {
    return '';
  }

  const parser = new DOMParser();
  const document = parser.parseFromString(html, 'text/html');
  const normalizedHtml = Array.from(document.body.childNodes).map(serializePastedNode).join('');

  return sanitizeJobHtml(normalizedHtml);
}

function getBulletMatch(line) {
  return line.match(/^\s*(?:[-*•◦▪●])\s+(.+)$/);
}

function getNumberedMatch(line) {
  return line.match(/^\s*\d+[.)]\s+(.+)$/);
}

function isHeadingLike(line) {
  const trimmed = line.trim();
  return trimmed.length <= 90 && /:$/.test(trimmed);
}

export function plainTextToJobHtml(text = '') {
  const lines = String(text || '')
    .replace(/\r\n?/g, '\n')
    .split('\n');

  const html = [];
  let listType = '';

  const closeList = () => {
    if (listType) {
      html.push(`</${listType}>`);
      listType = '';
    }
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    const bulletMatch = getBulletMatch(line);
    const numberedMatch = getNumberedMatch(line);

    if (!trimmed) {
      closeList();
      html.push('<p><br></p>');
      return;
    }

    if (bulletMatch || numberedMatch) {
      const nextListType = bulletMatch ? 'ul' : 'ol';
      if (listType !== nextListType) {
        closeList();
        html.push(`<${nextListType}>`);
        listType = nextListType;
      }
      html.push(`<li>${escapeHtml((bulletMatch || numberedMatch)[1])}</li>`);
      return;
    }

    closeList();

    if (isHeadingLike(line)) {
      html.push(`<p><strong>${escapeHtml(trimmed)}</strong></p>`);
      return;
    }

    html.push(`<p>${escapeHtml(trimmed)}</p>`);
  });

  closeList();

  return sanitizeJobHtml(html.join(''));
}

export function legacyBlocksToHtml(blocks = []) {
  return blocks
    .map((block) => {
      const text = escapeHtml(block.text || '').replace(/\n/g, '<br>');

      if (block.type === 'heading') {
        return `<h3>${text}</h3>`;
      }

      if (block.type === 'list' || block.type === 'numberedList') {
        const tag = block.type === 'numberedList' ? 'ol' : 'ul';
        const items = (block.items || []).map((item) => `<li>${escapeHtml(item)}</li>`).join('');
        return items ? `<${tag}>${items}</${tag}>` : '';
      }

      if (block.type === 'link' && block.href) {
        return `<p><a href="${escapeHtml(block.href)}">${text || escapeHtml(block.href)}</a></p>`;
      }

      if (block.type === 'divider') {
        return '';
      }

      return text ? `<p>${text}</p>` : '';
    })
    .filter(Boolean)
    .join('');
}
